import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import http from "../../../helpers/http";
import { doObjToFormData } from "../../../helpers/helpers";

/**
 * What this employer actually sponsored, from the Home Office release of
 * Certificates of Sponsorship by employer and occupation.
 *
 * This is the part of the page that is never empty. "Open Positions (0)" is
 * common and tells a member nothing; an employer in that file sponsored
 * something, and a third of them sponsored several different roles. So when
 * there are no vacancies this becomes the page's answer to "would they
 * sponsor someone like me", rather than the page having no answer at all.
 *
 * Fetched on its own rather than folded into company-details so the existing
 * endpoint is untouched: if this request fails the page renders exactly as it
 * does today, minus this block.
 */

const BLUE = "#1355ff";

const SponsoredRoles = ({ companyName, totalJobs }) => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    if (!companyName) return undefined;

    http
      .post("sponsor-occupations", doObjToFormData({ org_name: companyName }))
      .then(({ data }) => {
        if (!live) return;
        setRows(Array.isArray(data?.occupations) ? data.occupations : []);
        setLoaded(true);
      })
      .catch(() => {
        // Silent: this block is additional, not load-bearing. The page keeps
        // working without it, and an error here must never surface as a
        // failed request on a page the member paid to see.
        if (live) setLoaded(true);
      });

    return () => {
      live = false;
    };
  }, [companyName]);

  if (!loaded || rows.length === 0) return null;

  const published = rows.filter((r) => r.cos_total !== null);
  const floor = published.reduce((n, r) => n + Number(r.cos_total || 0), 0);
  const isFloor = published.length < rows.length;

  return (
    <div
      className="sponsored_roles"
      style={{
        background: "#FFFFFF",
        borderRadius: 16,
        padding: "26px 28px",
        marginTop: 20,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
      }}
    >
      <h2 style={{ margin: "0 0 5px", fontSize: 22, fontWeight: 700 }}>
        What they sponsor
      </h2>
      <p style={{ margin: "0 0 20px", fontSize: 14.5, color: "#5B6472" }}>
        Roles this employer assigned Certificates of Sponsorship for in 2025
        {isFloor ? ", at least " : ", "}
        {floor > 0 ? `${floor} in total.` : "."}
      </p>

      {rows.map((r) => (
        <div
          key={`${r.soc_code}-${r.soc_name}`}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            alignItems: "center",
            justifyContent: "space-between",
            padding: "15px 0",
            borderTop: "1px solid #EDF0F5",
          }}
        >
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <p
              style={{
                margin: "0 0 6px",
                fontSize: 15.5,
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {r.soc_name}
            </p>
            {r.salary_band ? (
              <span
                style={{
                  display: "inline-block",
                  background: "#F3F5F9",
                  color: "#5B6472",
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "4px 10px",
                  borderRadius: 8,
                }}
              >
                {r.salary_band}
              </span>
            ) : null}
          </div>
          <span
            style={{
              background: "#EBF0FF",
              color: BLUE,
              fontSize: 14,
              fontWeight: 600,
              padding: "7px 13px",
              borderRadius: 10,
              flex: "0 0 auto",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {r.cos_label}
          </span>
        </div>
      ))}

      {Number(totalJobs) === 0 ? (
        <p
          style={{
            margin: "20px 0 0",
            fontSize: 14.5,
            lineHeight: 1.6,
            color: "#3C4C68",
          }}
        >
          No live vacancies here right now. They sponsor these roles, so their
          careers page is worth watching —{" "}
          <Link to="/job-alerts" style={{ fontWeight: 600 }}>
            set a job alert
          </Link>
          .
        </p>
      ) : null}

      <p
        style={{
          margin: "18px 0 0",
          fontSize: 12.5,
          lineHeight: 1.7,
          color: "#8B94A3",
        }}
      >
        Home Office data released under the Freedom of Information Act. Counts
        of five or fewer are published as a range.
      </p>
    </div>
  );
};

export default SponsoredRoles;
